import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

export const ifasColors = {
  orange: "#EC6E00",
  deepOrange: "#D94F00",
  text: "#181C27",
  muted: "#6E7482",
  border: "#E6E2DC",
  /**
   * Row divider. Deliberately lighter and cooler than `border`: the approved
   * operations-queue mock uses `--border #E6E2DC` for panel edges and header
   * underlines, and `--rule #DCDFE4` for the dividers between table rows, so a
   * long list reads as one panel rather than a stack of boxes. Additive token —
   * nothing that renders `border` today changes.
   */
  rule: "#DCDFE4",
  surface: "#FFFFFF",
  page: "#F6F7F9",
  softOrange: "#FFF4EB",
  success: "#217A3D",
  info: "#1B56EB",
};

const resolveOperationColor = () => ifasColors.orange;

export const operationsTablePaperSx = {
  bgcolor: ifasColors.surface,
  overflow: "hidden",
  borderRadius: 2,
  border: `1px solid ${ifasColors.border}`,
  boxShadow: "0 10px 26px rgba(20, 24, 31, 0.06)",
  "& .MuiTableCell-root": {
    color: ifasColors.text,
    fontSize: 13.5,
  },
  "& .MuiIconButton-root": {
    width: 34,
    height: 34,
    color: "#6E7482",
  },
  "& .MuiIconButton-root:hover": {
    bgcolor: ifasColors.softOrange,
    color: ifasColors.orange,
  },
};

export const operationsTableHeadSx = {
  bgcolor: "#FBFAF8",
  "& .MuiTableCell-root": {
    color: "#5D6472",
    fontWeight: 900,
    fontSize: 11.5,
    letterSpacing: 0,
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${ifasColors.border}`,
    py: 1.15,
  },
};

export const operationsRowSx = {
  cursor: "pointer",
  transition: "background-color 160ms ease, box-shadow 160ms ease",
  "& .MuiTableCell-root": {
    py: 1.15,
    borderBottom: `1px solid ${ifasColors.border}`,
  },
  "& .MuiTableCell-root:first-of-type": {
    transition: "box-shadow 160ms ease",
  },
  "&:hover": {
    bgcolor: "#FFF9F4",
  },
  "&:hover .MuiTableCell-root:first-of-type": {
    boxShadow: `inset 3px 0 0 ${ifasColors.orange}`,
  },
};

/**
 * Keyboard-reachable variant of `operationsRowSx` for rows that behave like a
 * button (open a detail view). Adds a visible focus ring; the base
 * `operationsRowSx` is deliberately left untouched because it is also consumed
 * by non-IFAS screens.
 */
export const operationsClickableRowSx = {
  ...operationsRowSx,
  "&:focus-visible": {
    outline: `2px solid ${ifasColors.orange}`,
    outlineOffset: "-2px",
    bgcolor: "#FFF9F4",
  },
};

/**
 * Money cells: right-aligned with tabular figures so decimal points line up
 * down the column and digits do not reflow as values change.
 */
export const moneyCellSx = {
  textAlign: "right" as const,
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1',
  whiteSpace: "nowrap" as const,
};

/**
 * Accessible orange for TEXT. The brand orange (#EC6E00) measures ~2.9:1 on
 * white, below the WCAG AA 4.5:1 minimum for body text. This darkened tone
 * clears AA on white, #FAFAFA and the soft-orange chip background. Fills,
 * borders, icons and backgrounds keep the brand orange.
 */
export const ifasTextOrange = "#B45309";

/**
 * The shared theme paints buttons with a dark fill and white label, and its
 * disabled state keeps that dark fill while switching the label to near-black —
 * unreadable. theme.ts is shared with claims and must not change, so IFAS
 * buttons that can become disabled restate the disabled state locally.
 */
export const ifasDisabledBtnSx = {
  "&.Mui-disabled": {
    backgroundColor: "#F3F4F6",
    color: "#6B7280",
    borderColor: "#E5E7EB",
  },
};

export const operationsDetailPaperSx = {
  p: { xs: 1.75, md: 2.25 },
  borderRadius: 2,
  background: ifasColors.surface,
  border: `1px solid ${ifasColors.border}`,
  boxShadow: "0 10px 26px rgba(20, 24, 31, 0.06)",
  overflow: "hidden",
};

export const operationsActionBarSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: { xs: "flex-start", sm: "flex-end" },
  gap: 1,
  flexWrap: "wrap",
  "& .MuiButton-root": {
    minHeight: 38,
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
  },
};

export const getOperationsStatusSx = (status?: string) => {
  const normalizedStatus = String(status || "").toUpperCase();

  if (["AVAILABLE", "COMPLETED", "CONFIRMED"].includes(normalizedStatus)) {
    return { bgcolor: "#EAF7EF", color: "#217A3D" };
  }

  if (["ALLOCATED", "ASSIGNED"].includes(normalizedStatus)) {
    return { bgcolor: "#F0E9FF", color: "#6F42C1" };
  }

  if (["DISPATCHED", "UNDER_MAINTENANCE"].includes(normalizedStatus)) {
    return { bgcolor: "#FFF4EB", color: ifasColors.orange };
  }

  if (
    [
      "IN_PROGRESS",
      "IN_SERVICE",
      "BODY_COLLECTED",
      "REMOVAL_ORDER_CREATED",
      "DRIVER_ACCEPTED",
      "EN_ROUTE",
      "ARRIVED_AT_MORTUARY",
      "OFFERED",
    ].includes(normalizedStatus)
  ) {
    return { bgcolor: "#EAF1FF", color: "#1B56EB" };
  }

  if (["DRAFT", "PLANNED", "PENDING", "ARRIVED_AT_SCENE"].includes(normalizedStatus)) {
    return { bgcolor: "#FFF4EB", color: ifasColors.orange };
  }

  if (["CANCELLED", "DECLINED", "UNAVAILABLE", "OUT_OF_SERVICE", "REMOVED"].includes(normalizedStatus)) {
    return { bgcolor: "#FDECEC", color: "#C62828" };
  }

  return { bgcolor: "#EEEEEE", color: "#616161" };
};

export const formatOperationsStatus = (status?: string) =>
  String(status || "N/A")
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

/**
 * Confirmation gate for consequential, hard-to-undo IFAS actions (starting an
 * approval chain, converting a quotation into a tax invoice).
 */
export function OperationsConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="ifas-confirm-title"
    >
      <DialogTitle id="ifas-confirm-title" sx={{ fontWeight: 900 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ fontSize: 14, color: ifasColors.text }}>
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} sx={{ textTransform: "none", fontWeight: 800 }}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function OperationsPageShell({
  eyebrow = "IFAS Operations",
  title,
  description,
  meta,
  actions,
  children,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      p={{ xs: 1.5, md: 2 }}
      bgcolor={ifasColors.page}
      minHeight={{ xs: "auto", md: "100vh" }}
      width="100%"
      sx={{
        boxSizing: "border-box",
        overflowX: "hidden",
        textAlign: "left",
        background:
          "linear-gradient(180deg, #FFFFFF 0px, #F7F8FA 118px, #F6F7F9 100%)",
      }}
    >
      <Box
        sx={{
          mt: { xs: 7, sm: 0 },
          mb: 2,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          textAlign="left"
          maxWidth={820}
          sx={{
            display: "grid",
            gridTemplateColumns: "4px minmax(0, 1fr)",
            columnGap: 1.5,
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              width: 4,
              height: { xs: 78, md: 88 },
              borderRadius: 999,
              background: `linear-gradient(180deg, ${ifasColors.orange}, ${ifasColors.deepOrange})`,
              mt: 0.35,
            }}
          />
          <Box minWidth={0}>
          {eyebrow && (
            <Chip
              label={eyebrow}
              size="small"
              sx={{
                bgcolor: ifasColors.softOrange,
                color: ifasColors.orange,
                fontWeight: 900,
                borderRadius: 1.5,
                height: 24,
                mb: 0.8,
                "& .MuiChip-label": { px: 1.1 },
              }}
            />
          )}
          <Typography
            fontSize={{ xs: 22, md: 28 }}
            maxWidth={900}
            fontWeight={900}
            color={ifasColors.text}
            lineHeight={1.15}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              fontSize={{ xs: 14, md: 15 }}
              color={ifasColors.muted}
              lineHeight={1.65}
              mt={0.5}
            >
              {description}
            </Typography>
          )}
          {meta && (
            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
              {meta}
            </Box>
          )}
          </Box>
        </Box>
        {actions && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              width: { xs: "100%", md: "auto" },
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
      {children}
    </Box>
  );
}

export function OperationsMetricCard({
  label,
  value,
  color = ifasColors.orange,
  icon,
  caption,
}: {
  label: string;
  value: number | string;
  color?: string;
  icon?: ReactNode | string;
  caption?: string;
}) {
  const resolvedColor = resolveOperationColor();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.6,
        minHeight: 104,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${ifasColors.surface} 0%, ${alpha(
          resolvedColor,
          0.055,
        )} 100%)`,
        border: `1px solid ${ifasColors.border}`,
        borderLeft: `4px solid ${resolvedColor}`,
        boxShadow: "0 8px 20px rgba(20, 24, 31, 0.045)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: alpha(resolvedColor, 0.38),
          boxShadow: "0 12px 28px rgba(20, 24, 31, 0.08)",
        },
      }}
    >
      <Box minWidth={0}>
        <Typography
          fontSize={12}
          fontWeight={900}
          color="#6E7482"
          textTransform="uppercase"
          textAlign="left"
        >
          {label}
        </Typography>
        <Typography
          fontSize={{ xs: 25, md: 30 }}
          fontWeight={900}
          color={ifasColors.text}
          lineHeight={1.05}
          textAlign="left"
          mt={0.5}
        >
          {value}
        </Typography>
        {caption && (
          <Typography fontSize={12.5} color={ifasColors.muted} textAlign="left" mt={0.5}>
            {caption}
          </Typography>
        )}
      </Box>
      {icon && (
        <Box
          sx={{
            height: 42,
            width: 42,
            borderRadius: 2,
            bgcolor: alpha(resolvedColor, 0.12),
            color: resolvedColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "0 0 auto",
            "& svg": { fontSize: 24 },
          }}
        >
          {typeof icon === "string" ? (
            <img src={icon} alt={label} style={{ width: 30, height: 30 }} />
          ) : (
            icon
          )}
        </Box>
      )}
    </Paper>
  );
}

export function OperationsSectionHeader({
  title,
  meta,
}: {
  title: string;
  meta?: ReactNode;
}) {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={1.5}
      mb={1}
      flexWrap="wrap"
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            width: 6,
            height: 18,
            borderRadius: 999,
            bgcolor: ifasColors.orange,
          }}
        />
        <Typography fontWeight={900} color={ifasColors.text}>
          {title}
        </Typography>
      </Box>
      {meta && (
        <Typography fontSize={12.5} fontWeight={800} color={ifasColors.muted}>
          {meta}
        </Typography>
      )}
    </Box>
  );
}

export function OperationsEmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <Box
      sx={{
        py: 5,
        px: 2,
        textAlign: "center",
        color: ifasColors.muted,
      }}
    >
      <Typography fontWeight={900} color={ifasColors.text}>
        {title}
      </Typography>
      {description && (
        <Typography fontSize={13.5} mt={0.5}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

export function OperationsDetailGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
        },
        gap: 1.5,
        mt: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

export function OperationsDetailField({
  label,
  value,
  span = 1,
  muted = false,
}: {
  label: string;
  value?: ReactNode;
  span?: 1 | 2 | 3;
  muted?: boolean;
}) {
  const displayValue =
    value === undefined || value === null || value === "" ? "Not captured" : value;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.45,
        minHeight: 76,
        borderRadius: 2,
        border: `1px solid ${ifasColors.border}`,
        bgcolor: muted ? "#FAFAFA" : "#FFFFFF",
        gridColumn: {
          xs: "span 1",
          sm: span > 1 ? "span 2" : "span 1",
          lg: `span ${span}`,
        },
      }}
    >
      <Typography
        fontSize={11.5}
        fontWeight={900}
        color={ifasTextOrange}
        textAlign="left"
        sx={{ textTransform: "uppercase" }}
      >
        {label}
      </Typography>
      <Typography
        fontSize={14.5}
        fontWeight={800}
        color={displayValue === "Not captured" ? "#9AA0AA" : ifasColors.text}
        lineHeight={1.45}
        textAlign="left"
        mt={0.65}
        sx={{ wordBreak: "break-word" }}
      >
        {displayValue}
      </Typography>
    </Paper>
  );
}
